package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

data class SelectOption<T>(val value: T, val label: String, val disabled: Boolean = false)

@Composable
fun <T> Select(
    value: T,
    options: List<SelectOption<T>>,
    onValueChange: (T) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    help: String? = null,
    error: String? = null,
    disabled: Boolean = false,
    size: ControlSize = ControlSize.Md,
    placeholder: String? = null,
    ariaLabel: String? = null,
    hint: String? = null,
    required: Boolean = false,
    loading: Boolean = false,
    searchable: Boolean = false,
    popover: Boolean = searchable,
    reserveMessageSpace: Boolean = false,
) {
    val metrics = size.metrics
    FormField(
        label = label,
        modifier = modifier,
        hint = hint,
        help = help,
        error = error,
        required = required,
        reserveMessageSpace = reserveMessageSpace,
    ) { field ->
        if (popover || searchable) {
            ListboxSelect(
                value = value,
                options = options,
                onValueChange = onValueChange,
                metrics = metrics,
                field = field,
                searchable = searchable,
                disabled = disabled,
                loading = loading,
                placeholder = placeholder,
                ariaLabel = ariaLabel,
            )
        } else {
            NativeSelect(
                value = value,
                options = options,
                onValueChange = onValueChange,
                metrics = metrics,
                field = field,
                disabled = disabled,
                loading = loading,
                placeholder = placeholder,
                ariaLabel = ariaLabel,
            )
        }
    }
}

@Composable
private fun <T> NativeSelect(
    value: T,
    options: List<SelectOption<T>>,
    onValueChange: (T) -> Unit,
    metrics: ControlMetrics,
    field: FieldControlProps,
    disabled: Boolean,
    loading: Boolean,
    placeholder: String?,
    ariaLabel: String?,
) {
    val selectedIndex = options.indexOfFirst { it.value == value }
    val slots = if (field.invalid) 2 else 1

    Div(
        attrs = Modifier
            .styleModifier {
                property("position", "relative")
                property("width", "100%")
            }
            .toAttrs(),
    ) {
        Select(
            attrs = fieldBoxLook(metrics, slots)
                .styleModifier {
                    property("width", "100%")
                    property("cursor", if (disabled) "not-allowed" else "pointer")
                }
                .toAttrs {
                    if (disabled) attr("disabled", "")
                    if (loading) attr("aria-busy", "true")
                    ariaLabel?.let { attr("aria-label", it) }
                    fieldControl(field)
                    onChange { event ->
                        event.value?.toIntOrNull()
                            ?.let { options.getOrNull(it) }
                            ?.let { onValueChange(it.value) }
                    }
                },
        ) {
            placeholder?.let {
                Option(
                    value = "",
                    attrs = {
                        attr("disabled", "true")
                        if (selectedIndex < 0) attr("selected", "true")
                    },
                ) { Text(it) }
            }
            options.forEachIndexed { index, option ->
                Option(
                    value = index.toString(),
                    attrs = {
                        if (option.disabled) attr("disabled", "true")
                        if (index == selectedIndex) attr("selected", "true")
                    },
                ) { Text(option.label) }
            }
        }
        FieldAdornments(metrics.padX) {
            if (field.invalid) InvalidAdornment()
            AdornmentSlot {
                if (loading) Spinner(sizePx = AdornmentIconPx, ariaLabel = "") else LucideChevronDown(size = AdornmentIconPx)
            }
        }
    }
}
